import { type Ref, useCallback } from "react";
import { Control, FieldPath, FieldValues, useController } from "react-hook-form";
import { assignRef } from "../../lib/refs";
import { TextAreaInput } from "../inputs/TextAreaInput";

type FormTextAreaProps<TFieldValues extends FieldValues, TTransformedValues = TFieldValues> = {
	control: Control<TFieldValues, any, TTransformedValues>;
	name: FieldPath<TFieldValues>;
	className?: string;
	readonly?: boolean;
	placeholder?: string;
	hideErrorMessage?: boolean;
	testId?: string;
	id?: string;
	ref?: Ref<HTMLTextAreaElement>;
};

export const FormTextArea = <TFieldValues extends FieldValues, TTransformedValues = TFieldValues>({
	control,
	name,
	className,
	readonly,
	placeholder,
	hideErrorMessage = false,
	testId,
	id,
	ref: inputRef,
}: FormTextAreaProps<TFieldValues, TTransformedValues>) => {
	const {
		field: { ref: fieldRef, ...field },
		fieldState,
	} = useController({ control, name });
	const ref = useCallback(
		(node: HTMLTextAreaElement | null) => {
			fieldRef(node);
			assignRef(inputRef, node);
		},
		[fieldRef, inputRef],
	);

	return (
		<TextAreaInput
			{...field}
			id={id}
			ref={ref}
			readonly={readonly}
			error={hideErrorMessage ? undefined : fieldState.error}
			invalid={hideErrorMessage && !!fieldState.error}
			className={className}
			placeholder={placeholder}
			data-testid={testId}
		/>
	);
};
